// The silent video track of a voice message (docs/videonachricht-konzept.md), like the app's
// VideoTrackController/VideoTimeline: one recording of the camera that pauses while the picture is
// off, and segments that say which stretch of the audio has which stretch of the video:
// {at_ms (in the audio), video_ms (in the video), length_ms}. MP4 (H.264) where the browser records
// it, else WebM (decision 14); 1.2 Mbit/s, 720p, no sound.

export interface VideoSegment {
  at_ms: number;
  video_ms: number;
  length_ms: number;
}

export interface VideoResult {
  blob: Blob;
  mime: string;
  width: number;
  height: number;
  thumbnail?: Uint8Array<ArrayBuffer>;
  segments: VideoSegment[];
}

const MIN_LENGTH_MS = 300;
const THUMB_EDGE = 720;
const TYPES = ["video/mp4;codecs=avc1.42E01F", "video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];

/** The segments, cut to the audio's length; stretches under 300 ms dropped (their video time still counts). */
export function finishSegments(raw: VideoSegment[], durationMs: number): VideoSegment[] {
  return raw
    .filter((s) => s.length_ms >= MIN_LENGTH_MS && s.at_ms < durationMs)
    .map((s) => ({ ...s, length_ms: Math.min(s.length_ms, durationMs - s.at_ms) }))
    .filter((s) => s.length_ms > 0);
}

/** Where the video is at [positionMs] of the audio, or null where the message has no picture. */
export function videoAt(segments: VideoSegment[], positionMs: number): number | null {
  const s = segments.find((x) => positionMs >= x.at_ms && positionMs < x.at_ms + x.length_ms);
  return s ? s.video_ms + positionMs - s.at_ms : null;
}

export class VideoTrack {
  /** The camera's picture for the preview while recording. */
  stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private readonly chunks: Blob[] = [];
  private readonly segments: VideoSegment[] = [];
  private open: { at: number; video: number } | null = null;
  private videoMs = 0;
  private thumbnail: Uint8Array<ArrayBuffer> | undefined;
  private width = 0;
  private height = 0;
  private mime = "";
  private readonly audioMs: () => number;

  constructor(audioMs: () => number) {
    this.audioMs = audioMs;
  }

  get on(): boolean {
    return this.open !== null;
  }

  get used(): boolean {
    return this.recorder !== null;
  }

  /** Picture on: the camera starts (the browser asks the first time) and a segment opens. */
  async start(preview: HTMLVideoElement | null): Promise<void> {
    if (this.open) return;
    if (!this.recorder) {
      this.stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" }, audio: false });
      const settings = this.stream.getVideoTracks()[0]?.getSettings() ?? {};
      this.width = settings.width ?? 1280;
      this.height = settings.height ?? 720;
      this.mime = TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
      this.recorder = new MediaRecorder(this.stream, { mimeType: this.mime || undefined, videoBitsPerSecond: 1_200_000 });
      this.recorder.ondataavailable = (e) => e.data.size > 0 && this.chunks.push(e.data);
      this.recorder.start(1000);
      if (preview) {
        preview.srcObject = this.stream;
        void preview.play().catch(() => undefined);
        setTimeout(() => void this.snapshot(preview), 500);
      }
    } else {
      this.recorder.resume();
      for (const t of this.stream?.getVideoTracks() ?? []) t.enabled = true;
    }
    this.open = { at: this.audioMs(), video: this.videoMs };
  }

  /** Picture off: the segment closes, the recording of the camera pauses. */
  stop() {
    if (!this.open || !this.recorder) return;
    const length = this.audioMs() - this.open.at;
    if (length > 0) this.segments.push({ at_ms: this.open.at, video_ms: this.open.video, length_ms: length });
    this.videoMs += Math.max(0, length);
    this.open = null;
    if (this.recorder.state === "recording") this.recorder.pause();
    for (const t of this.stream?.getVideoTracks() ?? []) t.enabled = false;
  }

  /** The finished track, or nothing when the picture was never on long enough. */
  async finish(durationMs: number): Promise<VideoResult | null> {
    this.stop();
    const recorder = this.recorder;
    if (!recorder) return null;
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });
    this.release();
    const segments = finishSegments(this.segments, durationMs);
    if (segments.length === 0 || this.chunks.length === 0) return null;
    const mime = (this.mime || this.chunks[0]!.type || "video/webm").split(";")[0]!;
    return { blob: new Blob(this.chunks, { type: mime }), mime, width: this.width, height: this.height, thumbnail: this.thumbnail, segments };
  }

  cancel() {
    try {
      this.recorder?.stop();
    } catch {
      // Stopped already.
    }
    this.release();
  }

  private release() {
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    this.stream = null;
  }

  private async snapshot(video: HTMLVideoElement) {
    if (!video.videoWidth) return;
    const scale = Math.min(1, THUMB_EDGE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d")!.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.8));
    if (blob) this.thumbnail = new Uint8Array(await blob.arrayBuffer());
  }
}
